import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';

import type { ToolTrackingRecordViewModel } from 'ab-viewModels/equipment/equipmentPreview.viewModel';

import { formatDate } from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { ToolState, ToolStateLabel } from '@acceligentllc/shared/enums/toolState';
import { ToolStatus, ToolStatusLabel } from '@acceligentllc/shared/enums/toolStatus';

import { getFullClientUrl } from 'af-utils/http.util';

import SimpleTableControl from 'af-components/Controls/SimpleTable';
import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';
import Label from 'af-components/Label';
import LinkCell from 'af-components/Table/Cells/LinkCell';

import CLIENT from 'af-routes/client';

import styles from './styles.module.scss';

interface OwnProps {
	toolTrackingRecords: ToolTrackingRecordViewModel[];
	orgAlias: string;
	companyName: string;
}

type Props = OwnProps;

const SmallToolTable: React.FC<Props> = ({ toolTrackingRecords, orgAlias, companyName }) => {

	const rows = React.useMemo(() => {
		return toolTrackingRecords.slice().sort((a, b) => a.modelNumber.localeCompare(b.modelNumber));
	}, [toolTrackingRecords]);

	const renderSerialNumberCell = React.useCallback((_cell: CellContext<ToolTrackingRecordViewModel, string>) => {
		const id = _cell.row.original.toolId;
		const path = getFullClientUrl(orgAlias, CLIENT.COMPANY.EQUIPMENT.TOOL.PREVIEW(`${id}`, orgAlias, companyName));
		return <><LinkCell label="" path={path} />{_cell.row.original.serialNumber}</>;
	}, [companyName, orgAlias]);

	const renderStatusCell = React.useCallback((cell: CellContext<ToolTrackingRecordViewModel, string>) => {
		const status = cell.row.original.status;

		const text = ToolStatusLabel[status];

		switch (status) {
			case ToolStatus.IN_REPAIR:
			case ToolStatus.OUT_FOR_REPAIR:
			case ToolStatus.IN_SERVICE:
				return <Label className={'pending'} text={text} />;
			default:
				return <Label className={'completed'} text={text} />;
		}
	}, []);

	const renderStateCell = React.useCallback((cell: CellContext<ToolTrackingRecordViewModel, string>) => {
		const state = cell.row.original.state;

		const text = ToolStateLabel[state];

		switch (state) {
			case ToolState.INACTIVE:
				return <Label className={'inactive'} text={text} />;
			default:
				return <Label className={'completed'} text={text} />;
		}
	}, []);

	const columns: SimpleTableProps<ToolTrackingRecordViewModel>['columns'] = React.useMemo(() => [{
		id: 'modelNumber',
		cell: (_cell: CellContext<ToolTrackingRecordViewModel, string>) => {
			return _cell.getValue();
		},
		header: 'Model Number',
		accessor: 'modelNumber',
		enableSorting: true,
		size: 130,
	},
	{
		id: 'serialNumber',
		cell: renderSerialNumberCell,
		header: 'Serial Number',
		accessor: 'serialNumber',
		enableSorting: true,
		size: 130,
	},
	{
		id: 'type',
		cell: (_cell: CellContext<ToolTrackingRecordViewModel, number>) => {
			return _cell.getValue();
		},
		header: 'Type',
		accessor: 'type',
		enableSorting: true,
		size: 200,
	},
	{
		id: 'state',
		cell: renderStateCell,
		header: 'Status',
		accessor: 'state',
		enableSorting: true,
		size: 200,
	},
	{
		id: 'status',
		cell: renderStatusCell,
		header: 'Repair Status',
		accessor: 'status',
		enableSorting: true,
		size: 200,
	},
	{
		id: 'validFrom',
		cell: (_cell: CellContext<ToolTrackingRecordViewModel, number>) => {
			return formatDate(_cell.getValue(), TimeFormat.DATE_ONLY, TimeFormat.ISO_DATETIME);
		},
		header: 'Assigned to Equipment on',
		accessor: 'validFrom',
		enableSorting: false,
	}], [renderSerialNumberCell, renderStateCell, renderStatusCell]);

	return (
		<div className={styles.section}>
			<SimpleTableControl
				columns={columns}
				label="Small Tools Assigned to Equipment"
				labelTooltip="The list of all small tools that are currently assigned to specific equipment can be find on Equipment Preview page in Small Tools Assigned to Equipment table."
				rows={rows}
			/>
		</div>
	);
};

export default React.memo(SmallToolTable);
